import { Alert, Descriptions } from 'antd';
import { ArrowLeft, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DataTable } from '@/components/common/DataTable';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  taskPlatformApi,
  taskPlatformErrorMessage,
  WORKER_POOL_ID_PATTERN,
} from '@/services/taskPlatformApi';
import type { TaskExecution, WorkerPool } from '@/types/taskPlatform';
import {
  acceptanceTag,
  admissionTag,
  deliveryTag,
  executionStatusTag,
  formatTaskDuration,
  formatTaskTime,
} from './status';

type TaskRow = TaskExecution & { id: string };

function policyText(pool: WorkerPool, stage: 'execution' | 'delivery') {
  const policy =
    stage === 'execution' ? pool.executionPolicy : pool.deliveryPolicy;
  return `${policy.attemptTimeoutSeconds}s 超时 · ${policy.heartbeatSeconds}s 心跳 · 最多 ${policy.maxAttempts} 次`;
}

export function WorkerPoolDetailPage() {
  const navigate = useNavigate();
  const { workerPoolId = '' } = useParams();
  const [pool, setPool] = useState<WorkerPool | null>(null);
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [error, setError] = useState('');
  const [tasksError, setTasksError] = useState('');
  const [cursor, setCursor] = useState('');
  const [nextCursor, setNextCursor] = useState('');
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);

  const loadPool = useCallback(async () => {
    if (!WORKER_POOL_ID_PATTERN.test(workerPoolId)) {
      setError('Worker Pool ID 无效');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      setPool(await taskPlatformApi.getWorkerPool(workerPoolId));
    } catch (loadError) {
      setError(
        taskPlatformErrorMessage(loadError, 'Worker Pool 加载失败，请稍后重试'),
      );
    } finally {
      setLoading(false);
    }
  }, [workerPoolId]);

  const loadTasks = useCallback(async () => {
    if (!WORKER_POOL_ID_PATTERN.test(workerPoolId)) {
      return;
    }
    setTasksLoading(true);
    setTasksError('');
    try {
      const page = await taskPlatformApi.listTasks(workerPoolId, cursor, 20);
      setTasks(
        (page.tasks || []).map((task) => ({ ...task, id: task.taskId })),
      );
      setNextCursor(page.nextCursor || '');
    } catch (loadError) {
      setTasks([]);
      setNextCursor('');
      setTasksError(
        taskPlatformErrorMessage(loadError, '执行记录加载失败，请稍后重试'),
      );
    } finally {
      setTasksLoading(false);
    }
  }, [cursor, workerPoolId]);

  useEffect(() => {
    void loadPool();
  }, [loadPool]);

  useEffect(() => {
    void loadTasks();
  }, [loadTasks]);

  const columns = useMemo(
    () => [
      {
        id: 'taskId',
        header: 'Task ID',
        accessor: (row: TaskRow) => (
          <button
            type="button"
            className="max-w-[260px] truncate font-mono text-left text-primary hover:underline"
            title={row.taskId}
            onClick={() => navigate(`/tasks/executions/${row.taskId}`)}
          >
            {row.taskId}
          </button>
        ),
      },
      {
        id: 'acceptance',
        header: '接收',
        accessor: (row: TaskRow) => acceptanceTag(row.acceptance),
      },
      {
        id: 'executionStatus',
        header: '执行',
        accessor: (row: TaskRow) => executionStatusTag(row.executionStatus),
      },
      {
        id: 'deliveryStatus',
        header: '投递',
        accessor: (row: TaskRow) => deliveryTag(row.deliveryStatus),
      },
      {
        id: 'createdAt',
        header: '创建时间',
        accessor: (row: TaskRow) => formatTaskTime(row.createdAt),
      },
      {
        id: 'startedAt',
        header: '开始时间',
        accessor: (row: TaskRow) => formatTaskTime(row.startedAt),
      },
      {
        id: 'completedAt',
        header: '完成时间',
        accessor: (row: TaskRow) => formatTaskTime(row.completedAt),
      },
      {
        id: 'duration',
        header: '耗时',
        accessor: (row: TaskRow) =>
          formatTaskDuration(row.startedAt, row.completedAt, row.observedAt),
      },
    ],
    [navigate],
  );

  const goNext = () => {
    if (!nextCursor) return;
    setCursorHistory((history) => [...history, cursor]);
    setCursor(nextCursor);
  };

  const goPrevious = () => {
    setCursorHistory((history) => {
      if (history.length === 0) return history;
      setCursor(history[history.length - 1]);
      return history.slice(0, -1);
    });
  };

  if (error) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/tasks')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          返回任务
        </Button>
        <Alert type="error" showIcon message={error} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Button variant="ghost" onClick={() => navigate('/tasks')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          返回 Worker Pool
        </Button>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <h1 className="font-mono text-2xl font-semibold text-text-strong">
            {pool?.taskType || 'Worker Pool'}
          </h1>
          {pool ? admissionTag(pool.admissionEnabled) : null}
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          {pool
            ? `${pool.producer} · ${pool.executionLane} · ${pool.laneGeneration}`
            : '正在加载 Worker Pool…'}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Worker 配置</CardTitle>
          <CardDescription>
            接单状态为只读；不表示 Worker 进程在线状态。
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Descriptions bordered column={{ xs: 1, md: 2, xl: 3 }}>
            <Descriptions.Item label="Owner">
              {pool?.owner || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Definition">
              {pool ? `v${pool.definitionVersion}` : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Result Encoding">
              {pool?.resultEncoding || 'inline'}
            </Descriptions.Item>
            <Descriptions.Item label="Execute Handler">
              <span className="font-mono">{pool?.executeHandler || '—'}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Delivery Handler">
              <span className="font-mono">{pool?.deliveryHandler || '—'}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Workflow Timeout">
              {pool ? `${pool.workflowTimeoutSeconds}s` : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="执行策略">
              {pool ? policyText(pool, 'execution') : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="投递策略">
              {pool ? policyText(pool, 'delivery') : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="每副本并发">
              {pool
                ? `execute ${pool.executeConcurrencyPerReplica} / delivery ${pool.deliveryConcurrencyPerReplica}`
                : '—'}
            </Descriptions.Item>
          </Descriptions>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle>执行记录</CardTitle>
            <CardDescription>
              只显示当前 Worker Pool 下的任务，状态带有独立观测时间。
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => void loadTasks()}>
            <RefreshCw className="mr-1 h-3.5 w-3.5" />
            刷新
          </Button>
        </CardHeader>
        <CardContent>
          {tasksError ? (
            <Alert
              className="mb-4"
              type="warning"
              showIcon
              message={tasksError}
            />
          ) : null}
          <DataTable
            columns={columns}
            data={tasks}
            loading={loading || tasksLoading}
            emptyMessage="当前 Worker Pool 暂无执行记录"
          />
          <div className="mt-4 flex items-center justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={cursorHistory.length === 0 || tasksLoading}
              onClick={goPrevious}
            >
              <ChevronLeft className="mr-1 h-3.5 w-3.5" />
              上一页
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!nextCursor || tasksLoading}
              onClick={goNext}
            >
              下一页
              <ChevronRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
